import { Timestamp } from "firebase/firestore";


// export const ADMIN = 'a'; // either a1 or a2
export const ADMIN1 = 'a1';
export const ADMIN2 = 'a2';
export const USER1 = 'u1';
export const USER2 = 'u2';
export const NONE = 'none';


export const ROLE_ADMIN = 'a'; // group admin
export const ROLE_MEMBER = 'm';
export const ROLE_VIEWER = 'v';
export const ADMIN_TEXT = 'admin'; // group admin
export const MEMBER_TEXT = 'member';
export const VIEWER_TEXT = 'viewer';


export const STATUS_INVITED = 'i';
export const STATUS_ACTIVE = 'a';
export const STATUS_NOT_AVAILABLE = 'N/A'; // used when sys admins see users lists


export enum RoleType {
    admin = ROLE_ADMIN,
    member = ROLE_MEMBER,
    viewer = ROLE_VIEWER
}

export const ROLES = [
    // [RoleType.SYSADMIN]: {
    //     type: SYSADMIN,
    //     menu: 'System Admin',
    //     description: 'Full control over the entire platform, global settings, all groups, devices, and users.'
    // },
    {
        type: ROLE_ADMIN,
        menu: 'Admin',
        description: 'Full control over their groups settings, users, devices, and sharing permissions.'
    },
    {
        type: ROLE_MEMBER,
        menu: 'Member',
        description: 'Can view and use devices and data for the group, no administrative permissions.'
    },
    {
        type: ROLE_VIEWER,
        menu: 'Viewer',
        description: 'Read-only access to devices and data, cannot modify or manage devices or users.'
    }
];


export interface Email {
    to: string;
    subject: string;
    text: string;
    html: string;
}

export interface Sms {
    to: string;
    text: string;
}


export interface DateRange {
    start: Date;
    end: Date;
}


/* ******************************************************************************************************

########  ######## ##     ## ####  ######  ######## 
##     ## ##       ##     ##  ##  ##    ## ##       
##     ## ##       ##     ##  ##  ##       ##       
##     ## ######   ##     ##  ##  ##       ######   
##     ## ##        ##   ##   ##  ##       ##       
##     ## ##         ## ##    ##  ##    ## ##       
########  ########    ###    ####  ######  ######## 

****************************************************************************************************** */
export interface DeviceDB {
    id: string; // this is the system's internal id
    name: string;

    users: string[]; // users that own the device
    usersOrg: Array<{ user: string; org: string }>; // an array with users and their groups, this is useful when unsharing a device from a group

    firestoreDocId: string; // this is the firestore document id
    createdAt: any;
    createdByUid: string;

    claimedAt: any;
    claimedByUid: string;

    serialNumber: string;

    particleSku: string;
    particleDeviceId: string; // Particle device id
    particleProductId: string; // Particle product id

    // data: DeviceData | null;
    data: DeviceData | null;

    // tidal
    installHeight: number;
    timeZone: string;
    calibrationEnabled: boolean
    rawLow: number;
    rawHigh: number;
    referenceLow: number;
    referenceHigh: number;
    windOffset: number;

    location: Location | null;

}


// we can add in-memory fields to the Device interface
export interface Device extends DeviceDB {
    _groups: TeamMemberGroup[]; //  Array of groups the device is shared with, with the role of the user in them
    _dataTooOld: boolean;
    _lastSeen: string; // this is a string representation of the timestamp
    // _roles: TeamMemberOrgs[]; // Array of permissions for the user, inferred from the groups the user belongs to
    _logs: any; // Array of logs for the device
}

// Helper function to convert Device to DeviceDB
export function toDeviceDB(device: Device): DeviceDB {
    const {
        _groups, // In-memory field, not part of DeviceDB
        _dataTooOld,
        _lastSeen,
        ...deviceDB    // Spread the remaining fields into deviceDB
    } = device;

    return deviceDB;   // Return only the fields that belong to DeviceDB
}


export const EMPTY_DEVICE: Device = {
    id: '',
    name: '',
    users: [],
    usersOrg: [],
    firestoreDocId: '',
    createdAt: null,
    createdByUid: '',
    claimedAt: null,
    claimedByUid: '',
    serialNumber: '',
    particleSku: '',
    particleDeviceId: '',
    particleProductId: '',

    // tidal fields
    installHeight: 0,
    timeZone: '',
    calibrationEnabled: false,
    rawLow: 0,
    rawHigh: 0,
    referenceLow: 0,
    referenceHigh: 0,
    windOffset: 0,
    data: {
        tide: 0,
        wtemp: 0,
        wndspd: 0,
        wndr: 0,
        ppt: 0,
        ec: 0,
        intmp: 0,
        soc: 0,
        t: null,
        p: null,
        r: 0,
        b: 0,
        f: 0,
        v: 0,
        alt: 0,
    },
    // data: {
    //     soc: 0,
    //     t: null,
    //     p: null,
    //     r: 0,
    //     b: 0,
    //     f: 0,
    //     v: 0,
    // },

    location: {
        lat: 0,
        lon: 0,
        alt: 0,
        t: null,
        text: ''
    },

    // In-memory fields
    _groups: [],
    _dataTooOld: false,
    _lastSeen: '',
    _logs: [],

};

export interface Location {
    lat: number;
    lon: number;
    alt: number;
    t: Timestamp | null;
    text: string;
}

export const EMPTY_LOCATION: Location = {
    lat: 0,
    lon: 0,
    alt: 0,
    t: null,
    text: ''
};

export interface DeviceData {
    p: Timestamp | null;
    t: Timestamp | null;
    tide: number;
    wndspd: number; // mph
    wndr: number;
    wtemp: number;
    ppt: number;
    ec: number;
    intmp: number;
    soc: number;
    r: number; // pressure readings PSI
    b: number; // board temperature F
    f: number; // sensor temperature F
    v: number; // vCell voltage mV
    alt: number;
}

export interface DeviceDataAti {
    p: Timestamp | null; // published_at
    t: Timestamp | null;
    soc: number; // %
    r: number; // pressure readings PSI
    b: number; // board temperature F
    f: number; // sensor temperature F
    v: number; // vCell voltage mV
    alt: number;
}


/* ******************************************************************************************************

 #######  ########   ######      ###    ##    ## #### ########    ###    ######## ####  #######  ##    ## 
##     ## ##     ## ##    ##    ## ##   ###   ##  ##       ##    ## ##      ##     ##  ##     ## ###   ## 
##     ## ##     ## ##         ##   ##  ####  ##  ##      ##    ##   ##     ##     ##  ##     ## ####  ## 
##     ## ########  ##   #### ##     ## ## ## ##  ##     ##    ##     ##    ##     ##  ##     ## ## ## ## 
##     ## ##   ##   ##    ##  ######### ##  ####  ##    ##     #########    ##     ##  ##     ## ##  #### 
##     ## ##    ##  ##    ##  ##     ## ##   ###  ##   ##      ##     ##    ##     ##  ##     ## ##   ### 
 #######  ##     ##  ######   ##     ## ##    ## #### ######## ##     ##    ##    ####  #######  ##    ## 

****************************************************************************************************** */

export enum Subscription {
    None,
    Yearly,
    Monthly,
}

export interface UserNameRole {
    email: string;
    name: string;
    role: RoleType;
    status: string;  // STATUS_INVITED 'i', STATUS_ACTIVE 'a'
}

export interface GroupDB {
    id: string;
    name: string;
    createdByUid: string;
    devices: string[]; // this is only an array of device ids, not the devices themselves - for that we use _devices
    users: string[];
    usersInfo: Map<string, UserNameRole>; // a map with users, their emails, names and roles
    subscription: Subscription;
    createdAt: any;
}

export interface Group extends GroupDB {
    firestoreDocId: string; // this is the firestore document id
    _devices: Device[]; // Array of devices in the group
}

// Helper function to convert Device to DeviceDB
export function toGroupDB(group: Group): GroupDB {
    const {
        firestoreDocId,    // In-memory field, not part of GroupDB
        ...groupDB    // Spread the remaining fields
    } = group;

    return groupDB;   // Return only the fields that belong to OrgDB
}

export const EMPTY_GROUP: Group = {
    id: '',
    firestoreDocId: '',
    name: '',
    createdByUid: '',
    devices: [],
    users: [],
    usersInfo: new Map<string, UserNameRole>(),
    subscription: Subscription.None,
    createdAt: null,
    _devices: [],
};


/* ******************************************************************************************************

##     ##  ######  ######## ########  
##     ## ##    ## ##       ##     ## 
##     ## ##       ##       ##     ## 
##     ##  ######  ######   ########  
##     ##       ## ##       ##   ##   
##     ## ##    ## ##       ##    ##  
 #######   ######  ######## ##     ## 

****************************************************************************************************** */

export interface UserInfo {
    claims: any;
    email: string;
    name: string;
    doc: any;
    uid: string;
    admin: boolean;
}


// Define your UserDTO and regular User interfaces
export interface UserDB {
    createdAt: Timestamp | null; // => updated by createUser cloud function
    email: string;
    name: string;
    uid: string;
    claims: string;

    phone: string;
    emailNotifications: boolean;
    smsNotifications: boolean;

    lastSeen: Timestamp | null;

    invite: Invite | null;
}


export interface CompactUser {
    email: string;
    name: string;
    uid: string;
}


// we can add in-memory fields to the User interface
export interface User extends UserDB {
    orgs: TeamMemberGroup[];
    status: string; // STATUS_INVITED, STATUS_ACTIVE
}

// Helper function to convert User to toUserDB
export function toUserDB(user: User): UserDB {
    // const { _devicesSharedWithUser, ...userDB } = user;
    const { ...userDB } = user;
    return userDB;
}

export const EMPTY_USER: User = {
    uid: '',
    email: '',
    name: '',
    phone: '',
    claims: '',
    emailNotifications: true,
    smsNotifications: false,
    createdAt: null,
    lastSeen: null,
    invite: null,

    // in-memory fields
    orgs: [],
    status: ''
};

export interface Invite {
    createdByName: string;
    createdByUid: string;
    inviteSentAt: Timestamp | null;
    status: string;
}

export interface UserInvited {
    email: string;
    groupId: string;
    role: RoleType | null;

    createdAt: any;
    createdByUid: string;
    createdByName: string;

    status: string;
}

export const EMPTY_USER_INVITED: UserInvited = {
    email: '',
    groupId: '',
    role: null,
    createdAt: null,
    createdByUid: '',
    createdByName: '',
    status: STATUS_INVITED
};

export interface TeamMemberGroup {
    role: RoleType;
    id: string;
    name: string;
}

export interface TeamMember {
    uid: string;
    email: string;
    name: string;
    status: string; // STATUS_INVITED, STATUS_ACTIVE
    orgs: TeamMemberGroup[];
    devicesOwned: Device[]; // TODOGG: this does not belong here perhaps
    devicesSeen: Device[];  // TODOGG: this does not belong here perhaps
    claims: any;
    groups: Group[]; // the full group information, needed when we want to remove a user from a group
}

export const EMPTY_TEAM_MEMBER: TeamMember = {
    uid: '',
    email: '',
    name: '',
    status: STATUS_INVITED,
    orgs: [],
    devicesOwned: [],
    devicesSeen: [],
    claims: null,
    groups: []
};